import { Helmet } from 'react-helmet-async'

export interface SEOProps {
  /** Page title — appended as "{title} | {siteName}" */
  title?: string
  /** Meta description */
  description?: string
  /** Canonical URL for this page */
  url?: string
  /** OG image URL (1200x630 recommended) */
  image?: string
  /** Alt text for the OG image */
  imageAlt?: string
  /** Site name shown in OG tags (default: "Carousel") */
  siteName?: string
  /** Set true to add noindex, nofollow */
  noindex?: boolean
}

export function SEO({
  title,
  description,
  url,
  image,
  imageAlt,
  siteName = 'Carousel',
  noindex,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${siteName}` : siteName
  const alt = imageAlt ?? fullTitle

  return (
    <Helmet>
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {url && <link rel="canonical" href={url} />}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      {url && <meta property="og:url" content={url} />}
      {image && <meta property="og:image" content={image} />}
      {image && <meta property="og:image:width" content="1200" />}
      {image && <meta property="og:image:height" content="630" />}
      {image && <meta property="og:image:alt" content={alt} />}

      <meta name="twitter:card" content={image ? 'summary_large_image' : 'summary'} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}
      {image && <meta name="twitter:image:alt" content={alt} />}
    </Helmet>
  )
}
